
import React from 'react';
import {PhotoNavigationGuards} from './services/photos/usePhotoNavigationGuard';
import { createBrowserRouter, RouterProvider, Routes, Route, Navigate, Link } from 'react-router-dom';
import Layout from './components/Layout';
import { useAuth } from './services/authContext';
import { FeaturePrivilegeKey } from './types';
import UserManagement from './pages/UserManagement';
import FarmMapExplorer from './pages/FarmMapExplorer';
import Gallery from './pages/Gallery';
import JournalAccessBoundary from './components/JournalAccessBoundary';
import NaturePhotoDetail from './pages/NaturePhotoDetail';
import NatureWildlifePhotoDetail from './pages/NatureWildlifePhotoDetail';
import PhotoModeration from './pages/PhotoModeration';
import ModerationSettings, { ModerationRedirect } from './pages/ModerationSettings';
import LegacyPhotoDetail from './pages/LegacyPhotoDetail';
import Settings from './pages/Settings';
import Profile from './pages/Profile';
import ProjectInventory from './pages/ProjectInventory';
import ProjectDetail from './pages/ProjectDetail';
import AssetList from './pages/AssetList';
import AssetForm from './pages/AssetForm';
import AssetDetail from './pages/AssetDetail';
import DocumentVault from './pages/DocumentVault';
import DocumentDetail from './pages/DocumentDetail';
import DocumentPhotoDetail from './pages/DocumentPhotoDetail';
import EntityWorkPhotoDetail from './pages/EntityWorkPhotoDetail';
import FarmForm from './pages/FarmForm';
import AuthCallback from './pages/AuthCallback';
import LegacyWorkPhotoEntry from './pages/LegacyWorkPhotoEntry';
import MapFeatures from './pages/MapFeatures';
import AdminSettingsLayout from './components/AdminSettingsLayout';
import PoiCategorySettings from './components/PoiCategorySettings';
import PhotoSettings from './pages/PhotoSettings';
import SettingsApp from './pages/SettingsApp';
import GuestHome from './pages/GuestHome';

import VisitWelcome from './components/VisitWelcome';
import BadgeCelebration from './components/BadgeCelebration';
import CommunityBadges from './pages/CommunityBadges';
import BadgeDetail from './pages/BadgeDetail';
import SharedPatchCollection from './pages/SharedPatchCollection';
import BadgeSettings from './pages/BadgeSettings';
import { useGuest } from './services/GuestContext';
import PresentationBoundary from './components/PresentationBoundary';
import ContributionReview from './components/ContributionReview';
import RoutesPage from './pages/Routes';
import RouteDetail from './pages/RouteDetail';
import RoutePhotoDetail from './pages/RoutePhotoDetail';
import GpsSettings from './pages/GpsSettings';
import LiveListeningSettings from './pages/LiveListeningSettings';
import LocationReminder from './components/LocationReminder';
import WelcomeEntry from './pages/WelcomeEntry';
import EmailSettings from './pages/EmailSettings';
import NotificationSettings from './pages/NotificationSettings';
import Cameras from './pages/Cameras';
import CameraPhotoDetail from './pages/CameraPhotoDetail';
import PhotoAnimalReviewPage from './pages/PhotoAnimalReviewPage';
import CameraSettings from './pages/CameraSettings';
import VideoSettings from './pages/VideoSettings';
import PlaceDetail from './pages/PlaceDetail';
import PlacePhotoDetail from './pages/PlacePhotoDetail';
import PlaceCameraPhotoDetail from './pages/PlaceCameraPhotoDetail';
import OnboardingFlow from './components/OnboardingFlow';

const TerrainSettings = React.lazy(() => import('./pages/TerrainSettings'));
const NeighborSettings = React.lazy(() => import('./pages/NeighborSettings')); 

export const ProtectedRoute: React.FC<{ children: React.ReactNode; requiredKey?: FeaturePrivilegeKey; requireAdmin?: boolean; requireProperty?: boolean }> = ({ children, requiredKey, requireAdmin, requireProperty }) => {
  const { user, idToken, privileges, signIn, loading, refreshAccount } = useAuth();
  const { farm, loading: propertyLoading } = useGuest();
  if (loading) return <p role="status" className="p-8 text-center text-gray-600">Loading your account…</p>;

  const sessionExpired = !!user && !idToken && typeof navigator !== 'undefined' && navigator.onLine;
  if (!user || sessionExpired && (requiredKey || requireAdmin || requireProperty)) {
    return (
      <div className="flex min-h-screen items-center justify-center px-4 sm:block sm:min-h-0 sm:px-0">
        <div className="w-full max-w-2xl mx-auto bg-white shadow rounded-lg p-6 text-center border border-gray-200 sm:mt-12">
          <h2 className="text-xl font-semibold text-gray-900">{sessionExpired ? 'Sign in again' : 'Sign in required'}</h2>
          <p className="text-sm text-gray-600 mt-2">{sessionExpired ? 'Your session has expired. Your saved work is still on this device. Sign in with Google to continue.' : 'Please sign in with Google to continue.'}</p>
          <button
            className="mt-4 px-4 py-2 bg-usda-600 text-white rounded-md hover:bg-usda-700 disabled:opacity-60"
            onClick={() => signIn()}
            disabled={loading}
          >
            {loading ? 'Signing in...' : 'Sign in with Google'}
          </button>
        </div>
      </div>
    );
  }

  if (user.status !== 'Active') {
    return <div className="max-w-xl mx-auto rounded-2xl bg-white border p-8 mt-8"><h1 className="text-2xl font-semibold">{user.status==='Suspended'?'Access paused':'Your welcome is on its way'}</h1><p className="text-gray-600 mt-3">{user.status==='Suspended'?'Contact the property administrator to restore access.':'An administrator needs to approve your account and assign property access. Your Google account is signed in.'}</p><button className="mt-5 underline text-usda-700" onClick={()=>void refreshAccount().catch(()=>{})}>Check approval status</button></div>;
  }
  const denied = <div className="max-w-xl mx-auto rounded-2xl bg-white border p-8 mt-8"><h1 className="text-2xl font-semibold">This page needs additional access</h1><p className="text-gray-600 mt-3">Your current property permissions do not include this page. Contact the property administrator if you need access.</p>{privileges.map?.view && (!requireProperty || !!farm) && <Link to="/" className="inline-block mt-5 underline text-usda-700">Back to the field map</Link>}</div>;
  if (requireAdmin && !(user.roles || [user.role]).includes('Admin')) return denied;
  if (requiredKey && !privileges[requiredKey]?.view) return denied;
  if (requireProperty && !farm) {
    return propertyLoading ? <p role="status" className="p-8 text-center">Loading your property…</p> : denied;
  }

  return <>{children}</>;
};

const AppRoutes: React.FC = () => {
  return (
      <PhotoNavigationGuards><Layout>
        <PresentationBoundary />
        <OnboardingFlow />
        <JournalAccessBoundary />
        <VisitWelcome />
        <BadgeCelebration />
        <LocationReminder />
        <Routes>
          <Route path="/welcome" element={<WelcomeEntry />} />
          <Route path="/home" element={<ProtectedRoute><GuestHome /></ProtectedRoute>} />
          {/* Main Entry Point: Map Explorer */}
          <Route path="/" element={<ProtectedRoute requiredKey="map" requireProperty><FarmMapExplorer /></ProtectedRoute>} />
          
          {/* Projects */}
          <Route path="/projects" element={<ProtectedRoute requiredKey="projects"><ProjectInventory /></ProtectedRoute>} />
          <Route path="/projects/new" element={<ProtectedRoute requiredKey="projects"><ProjectDetail /></ProtectedRoute>} />
          <Route path="/projects/:id" element={<ProtectedRoute requiredKey="projects"><ProjectDetail /></ProtectedRoute>} />
          <Route path="/projects/:id/photos/:photoId" element={<ProtectedRoute requiredKey="projects" requireProperty><EntityWorkPhotoDetail kind="project" /></ProtectedRoute>} />
          <Route path="/maintenance" element={<Navigate to="/projects" replace />} />
          <Route path="/maintenance/new" element={<Navigate to="/projects/new" replace />} />
          <Route path="/maintenance/:id/edit" element={<Navigate to="/projects" replace />} />

          {/* Assets (Equipment) */}
          <Route path="/assets" element={<ProtectedRoute requiredKey="assets"><AssetList /></ProtectedRoute>} />
          <Route path="/assets/new" element={<ProtectedRoute requiredKey="assets"><AssetForm /></ProtectedRoute>} />
          <Route path="/assets/:id" element={<ProtectedRoute requiredKey="assets"><AssetDetail /></ProtectedRoute>} />
          <Route path="/assets/:id/photos/:photoId" element={<ProtectedRoute requiredKey="assets" requireProperty><EntityWorkPhotoDetail kind="asset" /></ProtectedRoute>} />
          <Route path="/assets/:id/edit" element={<ProtectedRoute requiredKey="assets"><AssetForm /></ProtectedRoute>} />
          <Route path="/map-features" element={<ProtectedRoute requiredKey="places"><MapFeatures /></ProtectedRoute>} />
          <Route path="/cameras" element={<ProtectedRoute requireProperty><Cameras /></ProtectedRoute>} />
          <Route path="/cameras/photos/:photoId" element={<ProtectedRoute requireProperty><CameraPhotoDetail /></ProtectedRoute>} />
          <Route path="/photos/:photoId/deer-review" element={<ProtectedRoute requireProperty><PhotoAnimalReviewPage /></ProtectedRoute>} />
          <Route path="/places/:id" element={<ProtectedRoute requiredKey="map" requireProperty><PlaceDetail /></ProtectedRoute>} />
          <Route path="/places/:id/photos/:photoId" element={<ProtectedRoute requiredKey="map" requireProperty><PlacePhotoDetail /></ProtectedRoute>} />
          <Route path="/places/:id/camera/photos/:photoId" element={<ProtectedRoute requiredKey="map" requireProperty><PlaceCameraPhotoDetail /></ProtectedRoute>} />

          {/* Farm Routes */}
          <Route path="/farms/new" element={<ProtectedRoute requiredKey="settings"><FarmForm /></ProtectedRoute>} />
          <Route path="/farms/:id/edit" element={<ProtectedRoute requiredKey="settings"><FarmForm /></ProtectedRoute>} />

          {/* Gallery */}
          <Route path="/work-photos" element={<Navigate to="/projects" replace />} />
          <Route path="/contributions" element={<ProtectedRoute requiredKey="contributionReview" requireProperty><ModerationRedirect section="contributions" /></ProtectedRoute>} />
          <Route path="/work-photos/:photoId" element={<ProtectedRoute requireProperty><LegacyWorkPhotoEntry /></ProtectedRoute>} />
          <Route path="/community/photos" element={<ProtectedRoute requiredKey="communityParticipation" requireProperty><Gallery /></ProtectedRoute>} />
          <Route path="/community/photo-review" element={<ProtectedRoute requiredKey="photoModeration" requireProperty><ModerationRedirect section="gallery" /></ProtectedRoute>} />
          <Route path="/community/photos/:photoId" element={<ProtectedRoute requiredKey="communityParticipation" requireProperty><NaturePhotoDetail /></ProtectedRoute>} />
          <Route path="/community/photos/wildlife/:view/:photoId" element={<ProtectedRoute requiredKey="communityParticipation" requireProperty><NatureWildlifePhotoDetail /></ProtectedRoute>} />
          <Route path="/gallery" element={<ProtectedRoute requiredKey="communityParticipation" requireProperty><Gallery /></ProtectedRoute>} />
          <Route path="/gallery/:photoId" element={<ProtectedRoute requireProperty><LegacyPhotoDetail /></ProtectedRoute>} />
          <Route path="/scouting" element={<ProtectedRoute requiredKey="communityParticipation" requireProperty><Gallery /></ProtectedRoute>} />
          <Route path="/scouting/:photoId" element={<ProtectedRoute requireProperty><LegacyPhotoDetail /></ProtectedRoute>} />

          {/* Document Vault */}
          <Route path="/documents" element={<ProtectedRoute requiredKey="documents"><DocumentVault /></ProtectedRoute>} />
          <Route path="/documents/:id" element={<ProtectedRoute requiredKey="documents"><DocumentDetail /></ProtectedRoute>} />
          <Route path="/documents/:id/photos/:photoId" element={<ProtectedRoute requiredKey="documents"><DocumentPhotoDetail /></ProtectedRoute>} />

          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/routes" element={<ProtectedRoute requiredKey="gpsTracking" requireProperty><RoutesPage /></ProtectedRoute>} />
          <Route path="/routes/:id/photos/:photoId" element={<ProtectedRoute requiredKey="gpsTracking" requireProperty><RoutePhotoDetail /></ProtectedRoute>} />
          <Route path="/routes/:id" element={<ProtectedRoute requiredKey="gpsTracking" requireProperty><RouteDetail /></ProtectedRoute>} />
          <Route path="/community/badges/collections/:collectionId" element={<ProtectedRoute requireProperty><SharedPatchCollection /></ProtectedRoute>} />
          <Route path="/community/badges" element={<ProtectedRoute requireProperty><CommunityBadges /></ProtectedRoute>} />
          <Route path="/community/badges/:badgeId" element={<ProtectedRoute requireProperty><BadgeDetail /></ProtectedRoute>} />
          <Route path="/community/leaders" element={<Navigate to="/community/badges" replace />} />
          <Route path="/settings/moderation" element={<ProtectedRoute requireProperty><AdminSettingsLayout moderationOnly /></ProtectedRoute>}>
            <Route element={<ModerationSettings />}>
              <Route index element={null} />
              <Route path="contributions" element={<ProtectedRoute requiredKey="contributionReview" requireProperty><ContributionReview /></ProtectedRoute>} />
              <Route path="gallery" element={<ProtectedRoute requiredKey="photoModeration" requireProperty><PhotoModeration /></ProtectedRoute>} />
            </Route>
          </Route>
          <Route path="/settings" element={<ProtectedRoute requiredKey="settings" requireAdmin><AdminSettingsLayout /></ProtectedRoute>}>
            <Route index element={<Settings />} />
            <Route path="welcome" element={<Navigate to="/home" replace />} />
            <Route path="emails" element={<EmailSettings />} />
            <Route path="notifications" element={<NotificationSettings />} />
            <Route path="badges" element={<BadgeSettings />} />
            <Route path="places" element={<PoiCategorySettings />} />
            <Route path="terrain" element={<React.Suspense fallback={<p role="status">Loading terrain settings…</p>}><TerrainSettings /></React.Suspense>} />
            <Route path="neighbors" element={<React.Suspense fallback={<p role="status">Loading neighbor settings…</p>}><NeighborSettings /></React.Suspense>} />
            <Route path="photos/*" element={<PhotoSettings />} />
            <Route path="cameras" element={<CameraSettings />} />
            <Route path="videos" element={<VideoSettings />} />
            <Route path="users" element={<UserManagement />} />
            <Route path="app" element={<SettingsApp />} />
            <Route path="gps" element={<GpsSettings />} />
            <Route path="live-listening" element={<LiveListeningSettings />} />
          </Route>
          <Route path="/auth/callback" element={<AuthCallback />} />

          {/* Temporary redirects for retired IA paths */}
          <Route path="/fields" element={<Navigate to="/" replace />} />
          <Route path="/fields/:id" element={<Navigate to="/" replace />} />
          <Route path="/fields/:id/edit" element={<Navigate to="/" replace />} />
          <Route path="/tracts/:id" element={<Navigate to="/" replace />} />
          <Route path="/tracts/:id/edit" element={<Navigate to="/" replace />} />
          <Route path="/tracts/:tractId/fields/new" element={<Navigate to="/" replace />} />
          <Route path="/farms/:farmId/tracts/new" element={<Navigate to="/" replace />} />
          <Route path="/contracts" element={<Navigate to="/" replace />} />
          <Route path="/contracts/new" element={<Navigate to="/" replace />} />
          <Route path="/contracts/:id" element={<Navigate to="/" replace />} />
          <Route path="/contracts/:id/edit" element={<Navigate to="/" replace />} />
          <Route path="/contracts/:contractId/practices/new" element={<Navigate to="/" replace />} />
          <Route path="/practices/:id/edit" element={<Navigate to="/" replace />} />
          <Route path="/practices/:practiceId/fields/new" element={<Navigate to="/" replace />} />
          <Route path="/contract-fields/:id/edit" element={<Navigate to="/" replace />} />
          <Route path="/programs" element={<Navigate to="/settings" replace />} />
          <Route path="/expenses" element={<Navigate to="/" replace />} />
          <Route path="/transactions/new" element={<Navigate to="/" replace />} />
          <Route path="/transactions/:id/edit" element={<Navigate to="/" replace />} />
          <Route path="/contacts" element={<Navigate to="/settings" replace />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Layout></PhotoNavigationGuards>
  );
};

// A data-router history allows the shared draft guard to protect native Back as
// well as links. Existing route elements and authorization boundaries stay here.
const router=createBrowserRouter([{path:'*',element:<AppRoutes/>}]);
export default function App(){return <RouterProvider router={router}/>;}
